import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import type { ContestType } from '@betterpool/api-types';

import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import type {
  PublicBoost,
  VoucherResponseDto,
  BonusDefinition,
} from '~/domains/promotions/types';
import { VoucherType } from '~/domains/promotions/types';
import { Routes } from '~/domains/common/constants/routes';
import { useQuickPicksPromotionValidation } from '~/domains/promotions/hooks/useQuickPicksPromotionValidation';
import {
  reportPromotionApply,
  reportPromotionRemove,
} from '~/domains/promotions/utils/analytics';
import type { PromotionActionLocation } from '~/domains/promotions/constants/analytics-enums';
import useProtectedGuard from '~/domains/protectionGuards/hooks/useProtectedGuard';
import { ProtectionGuardType } from '~/domains/protectionGuards/types';

import { normalizeBonus } from '../utils/formatters';

import {
  type PromotionsContextValue,
  type PromotionValidationErrors,
  type ValidatePromotionOptions,
} from './PromotionsContext';
import {
  readPromotionSelection,
  writePromotionSelection,
  readLegacyPromotionSnapshots,
} from './promotionsStorage';


const QUICK_PICKS_BOARD_PATHNAME = '/quick-picks/board';

/**
 * Owns selection state, mutual exclusivity, localStorage persistence, and the
 * high-level user actions (`selectBoost` / `selectVoucher`) that fire
 * analytics, navigate to the QuickPicks board on apply, and route
 * type-restricted vouchers to their destination.
 */
export const usePromotionsProvider = (): PromotionsContextValue => {
  const router = useRouter();
  const isOnQuickPicksBoard = router.pathname === QUICK_PICKS_BOARD_PATHNAME;
  const isOnContestPage = router.pathname.includes('/contest/');

  const [selectedBoost, setSelectedBoost] = useState<PublicBoost | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<VoucherResponseDto | null>(null);

  const { openModalUnique } = useContext(RootModalsContext);
  const { runProtectionChecks } = useProtectedGuard({
    action: 'make a deposit',
    protectionLevels: [
      ProtectionGuardType.KYC,
      ProtectionGuardType.LOCATION,
      ProtectionGuardType.GEOCOMPLY,
    ],
  });

  // Rehydrate on mount (client-only). One-time migration from the
  // legacy QuickPicks entry-state key when the new key is empty.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const stored = readPromotionSelection();
    if (stored.selectedBoost || stored.selectedVoucher) {
      setSelectedBoost(stored.selectedBoost);
      setSelectedVoucher(stored.selectedVoucher);
      return;
    }

    // @TODO: Remove after 1 week in production
    const legacy = readLegacyPromotionSnapshots();
    if (legacy) {
      setSelectedBoost(legacy.selectedBoost);
      setSelectedVoucher(legacy.selectedVoucher);
      writePromotionSelection(legacy);
    }
  }, []);

  // Persist on change. Skip the first effect run so we don't overwrite
  // localStorage with the initial empty state before hydration runs.
  const persistRef = useRef(false);
  useEffect(() => {
    if (!persistRef.current) {
      persistRef.current = true;
      return;
    }
    writePromotionSelection({ selectedBoost, selectedVoucher });
  }, [selectedBoost, selectedVoucher]);

  // ---------- High-level user actions ----------
  const selectBoost = useCallback(
    (boost: PublicBoost, location: PromotionActionLocation) => {
      if (selectedBoost?.id === boost.id) {
        setSelectedBoost(null);
        reportPromotionRemove({ location, promotion: boost });
        return;
      }
      setSelectedBoost(boost);
      setSelectedVoucher(null);

      reportPromotionApply({ location, promotion: boost });
      if (!isOnQuickPicksBoard) {
        router.push(Routes.quickPicksBoard());
      }
    },
    [selectedBoost, router, isOnQuickPicksBoard],
  );

  const selectVoucher = useCallback(
    (voucher: VoucherResponseDto, location: PromotionActionLocation) => {
      if (selectedVoucher?.id === voucher.id) {
        setSelectedVoucher(null);
        reportPromotionRemove({ location, promotion: voucher });
        return;
      }

      setSelectedVoucher(voucher);
      setSelectedBoost(null);
      reportPromotionApply({ location, promotion: voucher });

      // ----- Redirects -----
      // Do not redirect for general vouchers
      if (voucher.voucherType === VoucherType.GENERAL) {
        return;
      }

      // QP vouchers go to the QP board
      if (!isOnQuickPicksBoard && voucher.voucherType === VoucherType.QUICKPICKS) {
        router.push(Routes.quickPicksBoard());
        return;
      }

      // Contest id specific vouchers go to the contest detail page
      if (
        !isOnContestPage &&
        voucher.voucherType === VoucherType.CONTEST_ID_RESTRICTED &&
        voucher.restrictedContestId
      ) {
        router.push(Routes.contestDetail(voucher.restrictedContestId));
        return;
      }

      // Contest type specific vouchers go to the lobbyy
      if (!isOnContestPage && voucher.voucherType !== VoucherType.QUICKPICKS) {
        router.push(Routes.contestLobby({
          leagueName: voucher.league,
          contestType: voucher.contestType as ContestType
        }));
      }
    },
    [selectedVoucher, router, isOnQuickPicksBoard, isOnContestPage],
  );

  const selectDepositBonus = useCallback(
    async (bonus: BonusDefinition, location: PromotionActionLocation) => {
      const isAccessible = await runProtectionChecks();
      if (isAccessible) {
        reportPromotionApply({ location, promotion: normalizeBonus(bonus) });
        openModalUnique({ type: 'DepositModal' });
      }
    },
    [openModalUnique, runProtectionChecks],
  );

  // ---------- Bulk cleanup ----------
  const clearSelectedBonus = useCallback(() => {
    setSelectedBoost(null);
    setSelectedVoucher(null);
  }, []);

  // ---------- Low-level state mutators (no current callers; kept for future use) ----------
  const removeBoost = useCallback(() => {
    setSelectedBoost(null);
  }, []);

  const removeVoucher = useCallback(() => {
    setSelectedVoucher(null);
  }, []);

  // ---------- Validation ----------
  const { validatePromotion: rawValidatePromotion } = useQuickPicksPromotionValidation({
    selectedBoost,
    selectedVoucher,
  });

  const validatePromotion = useCallback(
    (options?: ValidatePromotionOptions): Promise<PromotionValidationErrors> =>
      rawValidatePromotion({
        picks: options?.picks,
        overrideEntryAmount: options?.amount,
        validateBoost: options?.validateBoost,
        validateVoucher: options?.validateVoucher,
      }),
    [rawValidatePromotion],
  );


  return useMemo<PromotionsContextValue>(
    () => ({
      selectedBoost,
      selectedVoucher,
      selectBoost,
      selectVoucher,
      selectDepositBonus,
      clearSelectedBonus,
      removeBoost,
      removeVoucher,
      validatePromotion,
    }),
    [
      selectedBoost,
      selectedVoucher,
      selectBoost,
      selectVoucher,
      selectDepositBonus,
      clearSelectedBonus,
      removeBoost,
      removeVoucher,
      validatePromotion,
    ],
  );
};
